import {
  createContext,
  createRef,
  MouseEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

import { t } from '@lingui/macro';

import { type ButtonProps } from '@decisiv/ui-components/lib/components/Button';
import { type ModalProps } from '@decisiv/ui-components/lib/components/Modal/types';

import useOpenBulkImport from 'hooks/useOpenBulkImport';

import { usePollContactBulkImportStatus } from '../hooks/usePollContactBulkImportStatus';
import { type BulkCreateContactsSavedState } from '../types';

// Lets the Wizard footer's Continue button trigger StepOne's submit (mirrors BulkCreateDepotModal).
export const stepOneSubmitRef = createRef<{ submit: () => void }>();

type PollStatus = ReturnType<typeof usePollContactBulkImportStatus>;

interface BulkCreateContactsHandles {
  stepOneProps: {
    nextButtonProps: ButtonProps;
  };
  wizardProps: {
    activeStep: number;
    intermediate: boolean;
    onCancel: NonNullable<ModalProps['onClose']>;
    submitButtonProps: ButtonProps;
    cancelButtonProps?: ButtonProps;
  };
  savedState: BulkCreateContactsSavedState;
  setSavedState: Dispatch<SetStateAction<BulkCreateContactsSavedState>>;
  setStepOneContinueButtonEnabled: Dispatch<SetStateAction<boolean>>;
  setStepOneContinueButtonLoading: Dispatch<SetStateAction<boolean>>;
  summary: PollStatus['summary'];
  reportUrl: PollStatus['reportUrl'];
  failedToResolve: PollStatus['failedToResolve'];
}

/* istanbul ignore next -- @preserve */
export const BulkCreateContactsContext =
  createContext<BulkCreateContactsHandles>({} as BulkCreateContactsHandles);

interface BulkCreateContactsProviderProps {
  children: ReactNode;
  onClose?: NonNullable<ModalProps['onClose']>;
}

const BulkCreateContactsProvider = ({
  children,
  onClose,
}: BulkCreateContactsProviderProps) => {
  const [savedState, setSavedState] = useState<BulkCreateContactsSavedState>(
    {},
  );

  const [stepOneContinueButtonEnabled, setStepOneContinueButtonEnabled] =
    useState(false);
  const [stepOneContinueButtonLoading, setStepOneContinueButtonLoading] =
    useState(false);

  const { startPolling, summary, reportUrl, pollingFinished, failedToResolve } =
    usePollContactBulkImportStatus();

  const { importId } = savedState;

  // While this modal is up it owns the outcome, shown on the summary step —
  // suppress the app-level toast. Closing the modal unmounts this provider,
  // which releases the claim and hands the outcome to the toast.
  useOpenBulkImport(importId);

  // Start polling once, as soon as the upload returns an import id.
  const startedRef = useRef(false);
  useEffect(() => {
    if (importId && !startedRef.current) {
      startedRef.current = true;
      startPolling(importId);
    }
  }, [importId, startPolling]);

  // Phase: form (no import yet) → processing (polling) → done (resolved).
  const processing = !!importId && !pollingFinished;
  const done = !!importId && pollingFinished;
  const activeStep = done ? 2 : 1;

  const hideModal = useCallback(
    (event: MouseEvent<unknown> | KeyboardEvent) => {
      onClose?.(event);
    },
    [onClose],
  );

  const stepOneProps = useMemo(
    () => ({
      nextButtonProps: {
        onClick: () => stepOneSubmitRef.current?.submit(),
        disabled: !stepOneContinueButtonEnabled,
        loading: stepOneContinueButtonLoading || processing,
        loadingText: processing ? t`Adding Contacts...` : t`Loading...`,
      },
    }),
    [stepOneContinueButtonEnabled, stepOneContinueButtonLoading, processing],
  );

  const wizardProps = useMemo(
    () => ({
      activeStep,
      intermediate: processing,
      onCancel: hideModal,
      submitButtonProps: {
        text: t`Close`,
        onClick: hideModal,
      },
      // While processing the wizard disables the primary button, so the visible
      // exit is the cancel slot — relabel it "Close" (the import keeps running).
      cancelButtonProps: processing
        ? { text: t`Close`, variant: 'normal' as const }
        : undefined,
    }),
    [activeStep, processing, hideModal],
  );

  const value = {
    stepOneProps,
    wizardProps,
    savedState,
    setSavedState,
    setStepOneContinueButtonEnabled,
    setStepOneContinueButtonLoading,
    summary,
    reportUrl,
    failedToResolve,
  } satisfies BulkCreateContactsHandles;

  return (
    <BulkCreateContactsContext.Provider value={value}>
      {children}
    </BulkCreateContactsContext.Provider>
  );
};

export const useBulkCreateContactsContext = () =>
  useContext(BulkCreateContactsContext);

export default BulkCreateContactsProvider;
